import Promise from 'promise';
import moment from 'moment-timezone';
import axios from 'axios';

// eslint-disable-next-line import/no-cycle
import Context from '@/data/Context';
import CustomerGroup from '@/data/CustomerGroup';
import IndustryClassification from '@/data/IndustryClassification';
import Collection from '@/lib/data/Collection';
import Role from '@/data/Role';
import Account from '@/data/Account';
import Currency from '@/data/Currency';
import Underwriter from '@/data/Underwriter';
import CustomerBroker from '@/data/CustomerBroker';
import CustomerDirector from '@/data/CustomerDirector';
import AddressDivision from '@/data/AddressDivision';
import AddressTradeCode from '@/data/AddressTradeCode';

/* eslint-disable no-param-reassign */
export default {
    state: {
        context: new Context(),
        customerGroupList: new Collection({
            model: CustomerGroup,
            url: '/customergroup/list',
        }),
        industryClassificationList: new Collection({
            model: IndustryClassification,
            url: '/customer/address/industry_classifications',
        }),
        accountList: new Collection({
            model: Account,
            url: '/customer/account/list',
            method: 'GET',
        }),
        currencyList: new Collection({
            model: Currency,
            url: '/currency/list',
            method: 'GET',
        }),
        underwriterList: new Collection({
            model: Underwriter,
            url: '/underwriter/list',
            method: 'GET',
        }),
        brokerList: new Collection({
            model: CustomerBroker,
            url: '/customer/broker/list',
            method: 'GET',
        }),
        directorList: new Collection({
            model: CustomerDirector,
            url: '/customer/director/list',
            method: 'GET',
        }),
        divisionList: new Collection({
            model: AddressDivision,
            url: '/customer/address/division/list',
            method: 'GET',
        }),
        addressTradeCodeList: new Collection({
            model: AddressTradeCode,
            url: '/customer/address/trade_code/list',
        }),
        dateValueFormat: 'YYYY-MM-DD',
        dateTimeValueFormat: 'YYYY-MM-DDTHH:mm:ss',
        dateDisplayMonthFormat: 'MMM, yyyy',
        dateDisplayFormat: 'MMM dd, yyyy',
        momentDisplayFormat: 'MMM DD, YYYY',
        momentDateTimeDisplayFormat: 'MMM DD, YYYY HH:mm',
        timeDisplayFormat: 'HH:mm',
        timeDisplay12HourFormat: 'hh:mm a',
        dateUserFormat: 'DD/MM/YYYY',
        sideNavCollapsed: true,
        sideNavHidden: false,
        langs: [
            {
                languageCode: 'tr',
                iso: 'tr',
                label: 'Türkçe',
            },
            {
                languageCode: 'en',
                iso: 'gb',
                label: 'English',
            },
            {
                languageCode: 'en_US',
                iso: 'us',
                label: 'English-US',
            },
            {
                languageCode: 'ro',
                iso: 'ro',
                label: 'Română',
            },
            {
                languageCode: 'fr',
                iso: 'fr',
                label: 'Français',
            },
            {
                languageCode: 'es',
                iso: 'es',
                label: 'Español',
            },
            {
                languageCode: 'de',
                iso: 'de',
                label: 'Deutsche',
            },
            {
                languageCode: 'pt',
                iso: 'pt',
                label: 'Português',
            },
            {
                languageCode: 'ar',
                iso: 'عربى',
                label: 'عربى',
            },
            {
                languageCode: 'el',
                iso: 'gr',
                label: 'Ελληνικά',
            },
        ],
        years: [2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025],
        currencies: ['USD', 'EUR', 'CHF', 'GBP'], // todo get from db
        agoraAppId: null,
        agoraAppCertificate: null,
        accessRoleList: new Collection({
            model: Role,
            url: '/role/list',
            method: 'GET',
        }),
    },
    mutations: {
        sideNavCollapsed: (state, value) => {
            state.sideNavCollapsed = value;
        },
        sideNavHidden: (state, value) => {
            state.sideNavHidden = value;
        },
        agoraAppId: (state, value) => {
            state.agoraAppId = value;
        },
        agoraAppCertificate: (state, value) => {
            state.agoraAppCertificate = value;
        },
    },
    getters: {
        user: (state) => state.context.user,
        isOrganizationRoot: (state) =>
            state.context.user.organization_id === '8e3b17ec-1c01-48f5-89f9-9a0982ea47bd',
        context: (state) => state.context,
        isContextUserRootAdmin: (state) => state.context.isRootAdmin,
        isGaf: (state) => state.context.organization.global_admin_flag,
        isRSA: (state) => state.context.organization.org_tag === 'rsa',
        isDamageNotice: (state) =>
            state.context.organization?.org_tag?.indexOf('damage-notice') > -1,
        customerGroupList: (state) => state.customerGroupList,
        industryClassificationList: (state) => state.industryClassificationList,
        accountList: (state) => state.accountList,
        currencyList: (state) => state.currencyList,
        underwriterList: (state) => state.underwriterList,
        brokerList: (state) => state.brokerList,
        directorList: (state) => state.directorList,
        divisionList: (state) => state.divisionList,
        addressTradeCodeList: (state) => state.addressTradeCodeList,
        dateUserFormat: (state) => state.dateUserFormat,
        dateValueFormat: (state) => state.dateValueFormat,
        dateTimeValueFormat: (state) => state.dateTimeValueFormat,
        dateDisplayFormat: (state) => state.dateDisplayFormat,
        dateDisplayMonthFormat: (state) => state.dateDisplayMonthFormat,
        momentDisplayFormat: (state) => state.momentDisplayFormat,
        momentDateTimeDisplayFormat: (state) => state.momentDateTimeDisplayFormat,
        timeDisplayFormat: (state) => state.timeDisplayFormat,
        timeDisplay12HourFormat: (state) => state.timeDisplay12HourFormat,
        sideNavCollapsed: (state) => state.sideNavCollapsed,
        sideNavHidden: (state) => state.sideNavHidden,
        getUser: (state, getters) => (id) => getters.context.users.find((item) => item.id === id),
        getLangs: (state) => state.langs.sort((a, b) => (a.languageCode < b.languageCode ? -1 : 1)),
        getYears: (state) => state.years,
        getCurrencies: (state) => state.currencies,
        agoraAppId: (state) => state.agoraAppId,
        agoraAppCertificate: (state) => state.agoraAppCertificate,
        features: (state) => state.context.organization.features,
        hasClaimManagement: (state, getters) =>
            getters.features.claim_management || getters.isContextUserRootAdmin,
        contextOrganization: (state) => state.context.organization,
        users: (state) => state.context.users,
        clientUsers: (state) => state.context.users.filter((item) => item.client_id),
        usersWithoutClients: (state) => state.context.users.filter((item) => !item.client_id),
        getCustomerId: (state, getters) => getters.user.client_id,
        accessRoleList: (state, getters) => {
            let list = state.accessRoleList;
            if (state.accessRoleList.length && !getters.hasClaimManagement) {
                list = state.accessRoleList.filter(
                    (item) =>
                        item.name != 'insured' ||
                        item.name != 'insurer_admin' ||
                        item.name != 'super_insured'
                );
            }
            return list;
        },
    },
    actions: {
        fetchContext(context) {
            return new Promise((resolve, reject) => {
                context.state.context.read({
                    beforesend: () => {
                        context.rootState.loading = true;
                    },
                    complete: () => {
                        context.rootState.loading = false;
                        context.rootState.loaded = true;
                    },
                    success: () => {
                        context.state.context.user.time_zone =
                            context.state.context.user.time_zone || moment.tz.guess();
                        moment.tz.setDefault(context.state.context.user.time_zone);
                        context.state.industryClassificationList.load();
                        resolve();
                    },
                    failure: () => {
                        reject();
                    },
                });
            });
        },
        fetchAgoraConfig(context) {
            return new Promise((resolve, reject) => {
                axios
                    .get('/f4403d4f-c157-45ad-9c18-0f6dca6dc55e?keyType=agora')
                    .then((configResponse) => {
                        const result = configResponse.data.data;

                        if (result.appId && result.appCertificate) {
                            context.commit('agoraAppId', result ? result.appId : null);
                            context.commit(
                                'agoraAppCertificate',
                                result ? result.appCertificate : null
                            );
                            resolve();
                        } else {
                            reject();
                        }
                    })
                    .catch(() => {
                        reject();
                    });
            });
        },
        startHeartbeat(context) {
            const authToken = Collection.getAuthToken();

            if (!authToken) {
                return;
            }

            clearInterval(context.state.heartbeatTimer);

            context.state.heartbeatTimer = setInterval(() => {
                context.state.context.heartbeat();
            }, 15000);
        },
    },
};
/* eslint-enable */
